import { useState, useMemo } from "react";
import { jsPDF } from "jspdf";
import {
  Plus, FileText, Download, Trash2, Eye, Calendar,
  DollarSign, Users, Megaphone, Gavel, Settings, ChevronDown, ChevronUp,
} from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import Field, { inputStyle, selectStyle } from "../components/Field";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import { fmt, fmtDate, getToday, generateId, exportCSV, maskCurrency, unmaskCurrency } from "../utils";

// ═══════════════════════════════════════
// Estilos reutilizáveis
// ═══════════════════════════════════════
const card = {
  background: "#fff", borderRadius: 10, padding: "16px 18px",
  border: "1px solid #e5e7eb",
};
const sectionHeader = (color) => ({
  display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
  borderRadius: 8, background: color + "10", borderLeft: `3px solid ${color}`,
  marginBottom: 12, cursor: "pointer", userSelect: "none",
});
const numInput = {
  ...inputStyle, width: 100, textAlign: "center", padding: "8px 10px",
};
const kpiBox = (color) => ({
  textAlign: "center", padding: "12px 8px", borderRadius: 8,
  background: color + "08", border: `1px solid ${color}20`,
  minWidth: 80, flex: "1 1 0",
});

// ═══════════════════════════════════════
// Dados iniciais de um relatório
// ═══════════════════════════════════════
const emptyReport = () => ({
  periodStart: "", periodEnd: "", reportTitle: "",
  // Financeiro
  finRevenue: 0, finRevenueDisplay: "",
  finInvoicesOpen: 0, finInvoicesPaid: 0,
  finSuppliersOpen: 0, finSuppliersOpenDisplay: "",
  finSuppliersDetail: [],
  // Credenciamento
  credContacted: 0, credAccredited: 0, credAwaiting: 0, credFailed: 0,
  // Comercial/Marketing
  comClientsContacted: 0, comClientsSuccess: 0, comClientsAwaiting: 0, comClientsFailed: 0,
  mktStories: 0, mktFeed: 0, mktReels: 0, mktOther: 0, mktPlatforms: "Instagram, Facebook, YouTube",
  // Licitações
  licInserted: 0, licProposalsSent: 0, licWon: 0, licInProgress: 0, licLost: 0,
  licPositions: "", licNotes: "",
  // Operações
  opsTrainings: 0, opsBugsFixed: 0, opsImprovements: 0, opsNotes: "",
  // Geral
  generalNotes: "",
});

const emptySupplier = () => ({
  name: "", clientName: "", amount: 0, amountDisplay: "", dueDate: "",
});

// ═══════════════════════════════════════
// Componente principal
// ═══════════════════════════════════════
export default function Management({ reports, setReports, userId, today }) {
  const [mode, setMode] = useState("list"); // list | form | view
  const [form, setForm] = useState(emptyReport());
  const [viewing, setViewing] = useState(null);
  const [openSections, setOpenSections] = useState({
    fin: true, cred: true, com: true, lic: true, ops: true, gen: true,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const toggleSection = (key) => setOpenSections((p) => ({ ...p, [key]: !p[key] }));
  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setNum = (field) => (e) => setForm((p) => ({ ...p, [field]: parseInt(e.target.value) || 0 }));

  const sorted = useMemo(
    () => [...reports].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")),
    [reports]
  );
  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  // ── Novo relatório ──
  const handleNew = () => {
    setForm({ ...emptyReport(), periodEnd: today, periodStart: today });
    setMode("form");
  };

  // ── Salvar ──
  const handleSave = () => {
    if (!form.periodStart || !form.periodEnd) return;
    const report = {
      id: generateId(),
      userId,
      periodStart: form.periodStart,
      periodEnd: form.periodEnd,
      reportTitle: form.reportTitle || `Relatório ${fmtDate(form.periodStart)} a ${fmtDate(form.periodEnd)}`,
      createdAt: getToday(),
      finRevenue: unmaskCurrency(form.finRevenueDisplay) || form.finRevenue || 0,
      finInvoicesOpen: form.finInvoicesOpen,
      finInvoicesPaid: form.finInvoicesPaid,
      finSuppliersOpen: unmaskCurrency(form.finSuppliersOpenDisplay) || form.finSuppliersOpen || 0,
      finSuppliersDetail: form.finSuppliersDetail.map((s) => ({
        ...s, amount: unmaskCurrency(s.amountDisplay) || s.amount || 0,
      })),
      credContacted: form.credContacted,
      credAccredited: form.credAccredited,
      credAwaiting: form.credAwaiting,
      credFailed: form.credFailed,
      credFailedRetryDate: form.credFailed > 0 ? addMonths(today, 3) : null,
      comClientsContacted: form.comClientsContacted,
      comClientsSuccess: form.comClientsSuccess,
      comClientsAwaiting: form.comClientsAwaiting,
      comClientsFailed: form.comClientsFailed,
      comClientsFailedRetry: form.comClientsFailed > 0 ? addMonths(today, 3) : null,
      mktStories: form.mktStories,
      mktFeed: form.mktFeed,
      mktReels: form.mktReels,
      mktOther: form.mktOther,
      mktPlatforms: form.mktPlatforms,
      licInserted: form.licInserted,
      licProposalsSent: form.licProposalsSent,
      licWon: form.licWon,
      licInProgress: form.licInProgress,
      licLost: form.licLost,
      licPositions: form.licPositions,
      licNotes: form.licNotes,
      opsTrainings: form.opsTrainings,
      opsBugsFixed: form.opsBugsFixed,
      opsImprovements: form.opsImprovements,
      opsNotes: form.opsNotes,
      generalNotes: form.generalNotes,
    };
    setReports((prev) => [...prev, report]);
    setMode("list");
  };

  // ── Excluir ──
  const handleDelete = (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este relatório?")) return;
    setReports((prev) => prev.filter((r) => r.id !== id));
  };

  // ── Visualizar ──
  const handleView = (r) => { setViewing(r); setMode("view"); };

  // ── Fornecedor ──
  const addSupplier = () => setForm((p) => ({
    ...p, finSuppliersDetail: [...p.finSuppliersDetail, emptySupplier()],
  }));
  const updateSupplier = (idx, field, value) => setForm((p) => {
    const arr = [...p.finSuppliersDetail];
    arr[idx] = { ...arr[idx], [field]: value };
    return { ...p, finSuppliersDetail: arr };
  });
  const removeSupplier = (idx) => setForm((p) => ({
    ...p, finSuppliersDetail: p.finSuppliersDetail.filter((_, i) => i !== idx),
  }));

  // ═══════════════════════════════════════
  // RENDER: LISTA DE RELATÓRIOS
  // ═══════════════════════════════════════
  if (mode === "list") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <GuideBanner
          title="Gerência — Relatórios Semanais"
          text="Alimente os dados de cada setor (Financeiro, Credenciamento, Comercial, Licitações, Operações) para gerar relatórios completos da reunião semanal. Exporte em PDF ou Excel."
        />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={handleNew} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "9px 18px",
            borderRadius: 8, border: "none", background: "#251C59", color: "#fff",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
            <Plus size={15} /> Novo Relatório
          </button>
        </div>

        {sorted.length === 0 ? (
          <div style={{ ...card, textAlign: "center", padding: 40, color: "#94a3b8" }}>
            <FileText size={32} style={{ margin: "0 auto 8px", display: "block" }} />
            <div style={{ fontSize: 13 }}>Nenhum relatório cadastrado ainda</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Clique em "Novo Relatório" para começar</div>
          </div>
        ) : (
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Título", "Período", "Receita", "Criado em", "Ações"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#64748b", fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((r) => (
                  <tr key={r.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1e293b" }}>{r.reportTitle}</td>
                    <td style={{ padding: "10px 14px", color: "#64748b", fontSize: 11 }}>{fmtDate(r.periodStart)} — {fmtDate(r.periodEnd)}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "#059669" }}>{fmt(r.finRevenue || 0)}</td>
                    <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 11 }}>{fmtDate(r.createdAt)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => handleView(r)} title="Visualizar" style={actionBtn("#3b82f6")}><Eye size={13} /></button>
                        <button onClick={() => exportReportPDF(r)} title="PDF" style={actionBtn("#ef4444")}><Download size={13} /></button>
                        <button onClick={() => exportReportExcel(r)} title="Excel" style={actionBtn("#059669")}><Download size={13} /></button>
                        <button onClick={() => handleDelete(r.id)} title="Excluir" style={actionBtn("#94a3b8")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalItems={sorted.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════
  // RENDER: VISUALIZAR RELATÓRIO
  // ═══════════════════════════════════════
  if (mode === "view" && viewing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <button onClick={() => { setMode("list"); setViewing(null); }} style={{
          alignSelf: "flex-start", padding: "6px 14px", borderRadius: 7,
          border: "1px solid #d1d5db", background: "#fff", color: "#475569",
          fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>
          ← Voltar
        </button>

        <div style={{ ...card }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>{viewing.reportTitle}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                Período: {fmtDate(viewing.periodStart)} a {fmtDate(viewing.periodEnd)} • Criado: {fmtDate(viewing.createdAt)}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => exportReportPDF(viewing)} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
                borderRadius: 7, border: "none", background: "#ef4444", color: "#fff",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>
                <Download size={13} /> PDF
              </button>
              <button onClick={() => exportReportExcel(viewing)} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
                borderRadius: 7, border: "none", background: "#059669", color: "#fff",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>
                <Download size={13} /> Excel
              </button>
            </div>
          </div>

          {/* KPI Resumo */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            <div style={kpiBox("#059669")}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#059669" }}>{fmt(viewing.finRevenue || 0)}</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Receita</div>
            </div>
            <div style={kpiBox("#7c3aed")}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#7c3aed" }}>{viewing.credAccredited || 0}</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Credenciados</div>
            </div>
            <div style={kpiBox("#3b82f6")}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#3b82f6" }}>{viewing.comClientsSuccess || 0}</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Vendas</div>
            </div>
            <div style={kpiBox("#f59e0b")}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }}>{viewing.licWon || 0}</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Licitações Ganhas</div>
            </div>
            <div style={kpiBox("#0d9488")}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#0d9488" }}>{viewing.opsTrainings || 0}</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Treinamentos</div>
            </div>
          </div>

          {/* Seções detalhadas */}
          <ViewSection title="Financeiro" color="#059669" items={[
            { label: "Receita Recebida", value: fmt(viewing.finRevenue || 0) },
            { label: "NFs em Aberto", value: viewing.finInvoicesOpen },
            { label: "NFs Pagas", value: viewing.finInvoicesPaid },
            { label: "Fornecedores em Aberto", value: fmt(viewing.finSuppliersOpen || 0) },
          ]} />
          {viewing.finSuppliersDetail && viewing.finSuppliersDetail.length > 0 && (
            <div style={{ marginBottom: 16, marginLeft: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Detalhamento Fornecedores:</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead><tr style={{ background: "#f8fafc" }}>
                  {["Fornecedor", "Cliente Atendido", "Valor", "Vencimento"].map((h) => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, color: "#64748b", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{(viewing.finSuppliersDetail || []).map((s, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "6px 10px", fontWeight: 600 }}>{s.name}</td>
                    <td style={{ padding: "6px 10px" }}>{s.clientName}</td>
                    <td style={{ padding: "6px 10px", color: "#ef4444", fontWeight: 600 }}>{fmt(s.amount || 0)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtDate(s.dueDate)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          <ViewSection title="Credenciamento de Parceiros" color="#7c3aed" items={[
            { label: "Contatados", value: viewing.credContacted },
            { label: "Credenciados", value: viewing.credAccredited },
            { label: "Aguardando Resposta", value: viewing.credAwaiting },
            { label: "Fracassaram", value: viewing.credFailed },
            ...(viewing.credFailedRetryDate ? [{ label: "Retorno agendado", value: fmtDate(viewing.credFailedRetryDate) }] : []),
          ]} />

          <ViewSection title="Comercial / Marketing" color="#3b82f6" items={[
            { label: "Clientes Contatados", value: viewing.comClientsContacted },
            { label: "Sucesso", value: viewing.comClientsSuccess },
            { label: "Aguardando Resposta", value: viewing.comClientsAwaiting },
            { label: "Fracassaram", value: viewing.comClientsFailed },
            ...(viewing.comClientsFailedRetry ? [{ label: "Retorno agendado", value: fmtDate(viewing.comClientsFailedRetry) }] : []),
            { label: "Stories", value: viewing.mktStories },
            { label: "Feed", value: viewing.mktFeed },
            { label: "Reels", value: viewing.mktReels },
            { label: "Outros", value: viewing.mktOther },
            { label: "Plataformas", value: viewing.mktPlatforms },
          ]} />

          <ViewSection title="Licitações" color="#f59e0b" items={[
            { label: "Inseridas no Portal", value: viewing.licInserted },
            { label: "Propostas Enviadas", value: viewing.licProposalsSent },
            { label: "Ganhas", value: viewing.licWon },
            { label: "Em Andamento", value: viewing.licInProgress },
            { label: "Perdidas", value: viewing.licLost },
            ...(viewing.licPositions ? [{ label: "Posições", value: viewing.licPositions }] : []),
            ...(viewing.licNotes ? [{ label: "Observações", value: viewing.licNotes }] : []),
          ]} />

          <ViewSection title="Operações e Suporte" color="#0d9488" items={[
            { label: "Treinamentos Realizados", value: viewing.opsTrainings },
            { label: "Bugs Corrigidos", value: viewing.opsBugsFixed },
            { label: "Melhorias Implementadas", value: viewing.opsImprovements },
            ...(viewing.opsNotes ? [{ label: "Observações", value: viewing.opsNotes }] : []),
          ]} />

          {viewing.generalNotes && (
            <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>Observações Gerais</div>
              <div style={{ fontSize: 12, color: "#475569", whiteSpace: "pre-wrap" }}>{viewing.generalNotes}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // RENDER: FORMULÁRIO
  // ═══════════════════════════════════════
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={() => setMode("list")} style={{
          padding: "6px 14px", borderRadius: 7, border: "1px solid #d1d5db",
          background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>
          ← Voltar
        </button>
        <button onClick={handleSave} style={{
          display: "flex", alignItems: "center", gap: 5, padding: "9px 18px",
          borderRadius: 8, border: "none", background: "#059669", color: "#fff",
          fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>
          Salvar Relatório
        </button>
      </div>

      {/* Período */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>
          <Calendar size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />
          Período e Identificação
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12 }}>
          <Field label="Data Início" required>
            <input type="date" style={inputStyle} value={form.periodStart} onChange={set("periodStart")} />
          </Field>
          <Field label="Data Fim" required>
            <input type="date" style={inputStyle} value={form.periodEnd} onChange={set("periodEnd")} />
          </Field>
          <Field label="Título do Relatório">
            <input style={inputStyle} value={form.reportTitle} onChange={set("reportTitle")} placeholder="Ex: Reunião Semanal 14/04 a 18/04" />
          </Field>
        </div>
      </div>

      {/* ── FINANCEIRO ── */}
      <div style={card}>
        <div style={sectionHeader("#059669")} onClick={() => toggleSection("fin")}>
          <DollarSign size={16} color="#059669" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Financeiro</span>
          {openSections.fin ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
        </div>
        {openSections.fin && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              <Field label="Valor Recebido (R$)">
                <input style={numInput} value={form.finRevenueDisplay} onChange={(e) => setForm((p) => ({ ...p, finRevenueDisplay: maskCurrency(e.target.value) }))} placeholder="0,00" />
              </Field>
              <Field label="NFs em Aberto">
                <input type="number" min="0" style={numInput} value={form.finInvoicesOpen} onChange={setNum("finInvoicesOpen")} />
              </Field>
              <Field label="NFs Pagas">
                <input type="number" min="0" style={numInput} value={form.finInvoicesPaid} onChange={setNum("finInvoicesPaid")} />
              </Field>
              <Field label="Valor Fornecedores Aberto (R$)">
                <input style={numInput} value={form.finSuppliersOpenDisplay} onChange={(e) => setForm((p) => ({ ...p, finSuppliersOpenDisplay: maskCurrency(e.target.value) }))} placeholder="0,00" />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Detalhamento de Fornecedores</span>
                <button onClick={addSupplier} style={{
                  display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
                  borderRadius: 6, border: "1px solid #d1d5db", background: "#fff",
                  fontSize: 11, color: "#475569", cursor: "pointer",
                }}>
                  <Plus size={12} /> Adicionar
                </button>
              </div>
              {form.finSuppliersDetail.map((s, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto auto auto", gap: 8, alignItems: "end", marginBottom: 6 }}>
                  <Field label={i === 0 ? "Fornecedor" : ""}>
                    <input style={inputStyle} value={s.name} onChange={(e) => updateSupplier(i, "name", e.target.value)} placeholder="Nome do fornecedor" />
                  </Field>
                  <Field label={i === 0 ? "Cliente Atendido" : ""}>
                    <input style={inputStyle} value={s.clientName} onChange={(e) => updateSupplier(i, "clientName", e.target.value)} placeholder="Nome do cliente" />
                  </Field>
                  <Field label={i === 0 ? "Valor (R$)" : ""}>
                    <input style={{ ...numInput, width: 110 }} value={s.amountDisplay} onChange={(e) => updateSupplier(i, "amountDisplay", maskCurrency(e.target.value))} placeholder="0,00" />
                  </Field>
                  <Field label={i === 0 ? "Vencimento" : ""}>
                    <input type="date" style={{ ...inputStyle, width: 140 }} value={s.dueDate} onChange={(e) => updateSupplier(i, "dueDate", e.target.value)} />
                  </Field>
                  <button onClick={() => removeSupplier(i)} style={{
                    padding: "8px", borderRadius: 6, border: "none",
                    background: "#fef2f2", color: "#ef4444", cursor: "pointer", marginBottom: 13,
                  }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── CREDENCIAMENTO ── */}
      <div style={card}>
        <div style={sectionHeader("#7c3aed")} onClick={() => toggleSection("cred")}>
          <Users size={16} color="#7c3aed" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Credenciamento de Parceiros</span>
          {openSections.cred ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
        </div>
        {openSections.cred && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <Field label="Contatados">
              <input type="number" min="0" style={numInput} value={form.credContacted} onChange={setNum("credContacted")} />
            </Field>
            <Field label="Credenciados">
              <input type="number" min="0" style={numInput} value={form.credAccredited} onChange={setNum("credAccredited")} />
            </Field>
            <Field label="Aguardando Resposta">
              <input type="number" min="0" style={numInput} value={form.credAwaiting} onChange={setNum("credAwaiting")} />
            </Field>
            <Field label="Fracassaram ↻ 3 meses">
              <input type="number" min="0" style={numInput} value={form.credFailed} onChange={setNum("credFailed")} />
            </Field>
          </div>
        )}
      </div>

      {/* ── COMERCIAL / MARKETING ── */}
      <div style={card}>
        <div style={sectionHeader("#3b82f6")} onClick={() => toggleSection("com")}>
          <Megaphone size={16} color="#3b82f6" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Comercial / Marketing</span>
          {openSections.com ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
        </div>
        {openSections.com && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Vendas</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <Field label="Clientes Contatados">
                <input type="number" min="0" style={numInput} value={form.comClientsContacted} onChange={setNum("comClientsContacted")} />
              </Field>
              <Field label="Sucesso">
                <input type="number" min="0" style={numInput} value={form.comClientsSuccess} onChange={setNum("comClientsSuccess")} />
              </Field>
              <Field label="Aguardando Resposta">
                <input type="number" min="0" style={numInput} value={form.comClientsAwaiting} onChange={setNum("comClientsAwaiting")} />
              </Field>
              <Field label="Fracassaram ↻ 3 meses">
                <input type="number" min="0" style={numInput} value={form.comClientsFailed} onChange={setNum("comClientsFailed")} />
              </Field>
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Redes Sociais</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 2fr", gap: 12 }}>
              <Field label="Stories">
                <input type="number" min="0" style={numInput} value={form.mktStories} onChange={setNum("mktStories")} />
              </Field>
              <Field label="Feed">
                <input type="number" min="0" style={numInput} value={form.mktFeed} onChange={setNum("mktFeed")} />
              </Field>
              <Field label="Reels">
                <input type="number" min="0" style={numInput} value={form.mktReels} onChange={setNum("mktReels")} />
              </Field>
              <Field label="Outros">
                <input type="number" min="0" style={numInput} value={form.mktOther} onChange={setNum("mktOther")} />
              </Field>
              <Field label="Plataformas">
                <input style={inputStyle} value={form.mktPlatforms} onChange={set("mktPlatforms")} placeholder="Instagram, Facebook, YouTube..." />
              </Field>
            </div>
          </>
        )}
      </div>

      {/* ── LICITAÇÕES ── */}
      <div style={card}>
        <div style={sectionHeader("#f59e0b")} onClick={() => toggleSection("lic")}>
          <Gavel size={16} color="#f59e0b" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Licitações</span>
          {openSections.lic ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
        </div>
        {openSections.lic && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <Field label="Inseridas no Portal">
                <input type="number" min="0" style={numInput} value={form.licInserted} onChange={setNum("licInserted")} />
              </Field>
              <Field label="Propostas Enviadas">
                <input type="number" min="0" style={numInput} value={form.licProposalsSent} onChange={setNum("licProposalsSent")} />
              </Field>
              <Field label="Ganhas">
                <input type="number" min="0" style={numInput} value={form.licWon} onChange={setNum("licWon")} />
              </Field>
              <Field label="Em Andamento">
                <input type="number" min="0" style={numInput} value={form.licInProgress} onChange={setNum("licInProgress")} />
              </Field>
              <Field label="Perdidas">
                <input type="number" min="0" style={numInput} value={form.licLost} onChange={setNum("licLost")} />
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Posições / Status Detalhado">
                <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={form.licPositions} onChange={set("licPositions")} placeholder="Descreva posições das licitações em andamento..." />
              </Field>
              <Field label="Observações (descontos altos, etc.)">
                <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={form.licNotes} onChange={set("licNotes")} placeholder="Monitoramento de contratos com descontos elevados..." />
              </Field>
            </div>
          </>
        )}
      </div>

      {/* ── OPERAÇÕES E SUPORTE ── */}
      <div style={card}>
        <div style={sectionHeader("#0d9488")} onClick={() => toggleSection("ops")}>
          <Settings size={16} color="#0d9488" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Operações e Suporte</span>
          {openSections.ops ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
        </div>
        {openSections.ops && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <Field label="Treinamentos Realizados">
                <input type="number" min="0" style={numInput} value={form.opsTrainings} onChange={setNum("opsTrainings")} />
              </Field>
              <Field label="Bugs Corrigidos">
                <input type="number" min="0" style={numInput} value={form.opsBugsFixed} onChange={setNum("opsBugsFixed")} />
              </Field>
              <Field label="Melhorias Implementadas">
                <input type="number" min="0" style={numInput} value={form.opsImprovements} onChange={setNum("opsImprovements")} />
              </Field>
            </div>
            <Field label="Observações de Operações">
              <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={form.opsNotes} onChange={set("opsNotes")} placeholder="Detalhes dos treinamentos, bugs e melhorias..." />
            </Field>
          </>
        )}
      </div>

      {/* ── OBSERVAÇÕES GERAIS ── */}
      <div style={card}>
        <div style={sectionHeader("#64748b")} onClick={() => toggleSection("gen")}>
          <FileText size={16} color="#64748b" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Observações Gerais</span>
          {openSections.gen ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
        </div>
        {openSections.gen && (
          <Field label="Notas gerais / visão geral da empresa">
            <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={form.generalNotes} onChange={set("generalNotes")} placeholder="Resumo geral da semana, pontos de atenção, destaques..." />
          </Field>
        )}
      </div>

      {/* Botão salvar final */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={() => setMode("list")} style={{
          padding: "9px 18px", borderRadius: 8, border: "1px solid #d1d5db",
          background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>
          Cancelar
        </button>
        <button onClick={handleSave} style={{
          display: "flex", alignItems: "center", gap: 5, padding: "9px 24px",
          borderRadius: 8, border: "none", background: "#059669", color: "#fff",
          fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>
          Salvar Relatório
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// Sub-componentes
// ═══════════════════════════════════════
function ViewSection({ title, color, items }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
        borderRadius: 6, background: color + "10", borderLeft: `3px solid ${color}`,
        marginBottom: 8,
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{title}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6, paddingLeft: 12 }}>
        {items.map((it, i) => (
          <div key={i} style={{ fontSize: 12, color: "#475569" }}>
            <span style={{ color: "#94a3b8" }}>{it.label}: </span>
            <strong style={{ color: "#1e293b" }}>{it.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function actionBtn(color) {
  return {
    padding: "5px 7px", borderRadius: 5, border: `1px solid ${color}30`,
    background: color + "08", color, cursor: "pointer", display: "flex",
    alignItems: "center",
  };
}

function addMonths(dateStr, months) {
  const d = new Date(dateStr + "T12:00:00");
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split("T")[0];
}

// ═══════════════════════════════════════
// EXPORT PDF
// ═══════════════════════════════════════
function exportReportPDF(r) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const primary = [37, 28, 89];
  const gray = [100, 116, 139];
  const darkText = [30, 41, 59];
  let y = 0;

  // Header
  doc.setFillColor(...primary);
  doc.rect(0, 0, W, 32, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("INSTASOLUTIONS", 14, 15);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Relatório de Gerência", 14, 22);
  doc.setFontSize(10);
  doc.text(fmtDate(r.createdAt), W - 14, 15, { align: "right" });
  doc.setFontSize(8);
  doc.text(`${fmtDate(r.periodStart)} a ${fmtDate(r.periodEnd)}`, W - 14, 21, { align: "right" });

  y = 40;
  doc.setTextColor(...darkText);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(r.reportTitle || "Relatório Semanal", 14, y);
  y += 10;

  const checkPage = (need) => {
    if (y + need > 280) { doc.addPage(); y = 15; }
  };

  // KPI row
  const kpis = [
    { label: "Receita", value: fmt(r.finRevenue || 0) },
    { label: "Credenciados", value: String(r.credAccredited || 0) },
    { label: "Vendas", value: String(r.comClientsSuccess || 0) },
    { label: "Lic. Ganhas", value: String(r.licWon || 0) },
    { label: "Treinamentos", value: String(r.opsTrainings || 0) },
  ];
  const cw = (W - 28 - 16) / 5;
  kpis.forEach((k, i) => {
    const x = 14 + i * (cw + 4);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, cw, 18, 2, 2, "F");
    doc.setTextColor(...primary);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(k.value, x + cw / 2, y + 8, { align: "center" });
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(k.label, x + cw / 2, y + 14, { align: "center" });
  });
  y += 26;

  const addSection = (title, color, rows) => {
    checkPage(10 + rows.length * 6);
    doc.setFillColor(...color);
    doc.roundedRect(14, y, W - 28, 7, 1, 1, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(title, 18, y + 5);
    y += 11;
    rows.forEach(([label, value]) => {
      checkPage(6);
      doc.setTextColor(...gray);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(label + ":", 18, y);
      doc.setTextColor(...darkText);
      doc.setFont("helvetica", "bold");
      doc.text(String(value), 80, y);
      y += 5.5;
    });
    y += 4;
  };

  addSection("FINANCEIRO", [5, 150, 105], [
    ["Receita Recebida", fmt(r.finRevenue || 0)],
    ["NFs em Aberto", r.finInvoicesOpen || 0],
    ["NFs Pagas", r.finInvoicesPaid || 0],
    ["Fornecedores em Aberto", fmt(r.finSuppliersOpen || 0)],
  ]);

  if (r.finSuppliersDetail && r.finSuppliersDetail.length > 0) {
    checkPage(8 + r.finSuppliersDetail.length * 5);
    doc.setTextColor(...gray);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Detalhamento de Fornecedores:", 18, y);
    y += 5;
    r.finSuppliersDetail.forEach((s) => {
      checkPage(5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...darkText);
      doc.text(`${s.name} → ${s.clientName} | ${fmt(s.amount || 0)} | Venc: ${fmtDate(s.dueDate)}`, 22, y);
      y += 4.5;
    });
    y += 4;
  }

  addSection("CREDENCIAMENTO DE PARCEIROS", [124, 58, 237], [
    ["Contatados", r.credContacted || 0],
    ["Credenciados", r.credAccredited || 0],
    ["Aguardando Resposta", r.credAwaiting || 0],
    ["Fracassaram", r.credFailed || 0],
    ...(r.credFailedRetryDate ? [["Retorno em", fmtDate(r.credFailedRetryDate)]] : []),
  ]);

  addSection("COMERCIAL / MARKETING", [59, 130, 246], [
    ["Clientes Contatados", r.comClientsContacted || 0],
    ["Sucesso", r.comClientsSuccess || 0],
    ["Aguardando Resposta", r.comClientsAwaiting || 0],
    ["Fracassaram", r.comClientsFailed || 0],
    ...(r.comClientsFailedRetry ? [["Retorno em", fmtDate(r.comClientsFailedRetry)]] : []),
    ["Stories", r.mktStories || 0],
    ["Feed", r.mktFeed || 0],
    ["Reels", r.mktReels || 0],
    ["Outros Posts", r.mktOther || 0],
    ["Plataformas", r.mktPlatforms || "—"],
  ]);

  addSection("LICITAÇÕES", [245, 158, 11], [
    ["Inseridas no Portal", r.licInserted || 0],
    ["Propostas Enviadas", r.licProposalsSent || 0],
    ["Ganhas", r.licWon || 0],
    ["Em Andamento", r.licInProgress || 0],
    ["Perdidas", r.licLost || 0],
    ...(r.licPositions ? [["Posições", r.licPositions]] : []),
    ...(r.licNotes ? [["Observações", r.licNotes]] : []),
  ]);

  addSection("OPERAÇÕES E SUPORTE", [13, 148, 136], [
    ["Treinamentos", r.opsTrainings || 0],
    ["Bugs Corrigidos", r.opsBugsFixed || 0],
    ["Melhorias", r.opsImprovements || 0],
    ...(r.opsNotes ? [["Observações", r.opsNotes]] : []),
  ]);

  if (r.generalNotes) {
    checkPage(15);
    doc.setFillColor(100, 116, 139);
    doc.roundedRect(14, y, W - 28, 7, 1, 1, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("OBSERVAÇÕES GERAIS", 18, y + 5);
    y += 11;
    doc.setTextColor(...darkText);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(r.generalNotes, W - 36);
    lines.forEach((line) => {
      checkPage(5);
      doc.text(line, 18, y);
      y += 4.5;
    });
  }

  // Footer
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text(`InstaSolutions CRM — Página ${i}/${pages}`, W / 2, 292, { align: "center" });
  }

  const safeName = (r.reportTitle || "relatorio").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 40);
  doc.save(`${safeName}_${r.periodStart}_${r.periodEnd}.pdf`);
}

// ═══════════════════════════════════════
// EXPORT EXCEL (CSV com separador ; para abrir no Excel BR)
// ═══════════════════════════════════════
function exportReportExcel(r) {
  // Resumo
  const summary = {
    "Título": r.reportTitle,
    "Período Início": r.periodStart,
    "Período Fim": r.periodEnd,
    "Criado em": r.createdAt,
    "Receita (R$)": r.finRevenue || 0,
    "NFs em Aberto": r.finInvoicesOpen || 0,
    "NFs Pagas": r.finInvoicesPaid || 0,
    "Fornecedores em Aberto (R$)": r.finSuppliersOpen || 0,
    "Fornecedores Contatados": r.credContacted || 0,
    "Fornecedores Credenciados": r.credAccredited || 0,
    "Fornecedores Aguardando": r.credAwaiting || 0,
    "Fornecedores Fracassaram": r.credFailed || 0,
    "Retorno Credenciamento": r.credFailedRetryDate || "",
    "Clientes Contatados": r.comClientsContacted || 0,
    "Clientes Sucesso": r.comClientsSuccess || 0,
    "Clientes Aguardando": r.comClientsAwaiting || 0,
    "Clientes Fracassaram": r.comClientsFailed || 0,
    "Retorno Clientes": r.comClientsFailedRetry || "",
    "Stories": r.mktStories || 0,
    "Feed": r.mktFeed || 0,
    "Reels": r.mktReels || 0,
    "Outros Posts": r.mktOther || 0,
    "Plataformas": r.mktPlatforms || "",
    "Licitações Inseridas": r.licInserted || 0,
    "Propostas Enviadas": r.licProposalsSent || 0,
    "Licitações Ganhas": r.licWon || 0,
    "Licitações Andamento": r.licInProgress || 0,
    "Licitações Perdidas": r.licLost || 0,
    "Posições Licitações": r.licPositions || "",
    "Obs Licitações": r.licNotes || "",
    "Treinamentos": r.opsTrainings || 0,
    "Bugs Corrigidos": r.opsBugsFixed || 0,
    "Melhorias": r.opsImprovements || 0,
    "Obs Operações": r.opsNotes || "",
    "Observações Gerais": r.generalNotes || "",
  };

  exportCSV([summary], `relatorio_gerencia_${r.periodStart}_${r.periodEnd}`);

  // Se tem fornecedores, exporta aba separada
  if (r.finSuppliersDetail && r.finSuppliersDetail.length > 0) {
    const suppliers = r.finSuppliersDetail.map((s) => ({
      "Fornecedor": s.name,
      "Cliente Atendido": s.clientName,
      "Valor (R$)": s.amount || 0,
      "Vencimento": s.dueDate || "",
    }));
    exportCSV(suppliers, `fornecedores_${r.periodStart}_${r.periodEnd}`);
  }
}
